import React, { useState, useEffect } from 'react';

const UserCurrentPermissionsModal = ({ isOpen, onClose, user, onRevokePermissions }) => {
  const [userPermissions, setUserPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      setUserPermissions(user.permissions || []);
      setSelectedPermissions([]); // Reset selection when modal opens
    }
  }, [isOpen, user]);

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleRevokeSelected = async () => {
    if (selectedPermissions.length === 0) return;

    setLoading(true);
    try {
      const submitData = {
        user_ids: [user.id],
        permission_ids: selectedPermissions,
        action: 'revoke'
      };

      await onRevokePermissions(submitData);
      setSelectedPermissions([]);
      // The parent will handle updating the user permissions and closing modal if needed
    } catch (error) {
      console.error('Error revoking permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '80vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '15px',
          borderBottom: '2px solid #E5E7EB'
        }}>
          <div>
            <h2 style={{ margin: 0, color: '#1F2937', fontSize: '20px', fontWeight: '600' }}>
              Current Permissions for {user?.full_name}
            </h2>
            <p style={{ margin: '5px 0 0 0', color: '#6B7280', fontSize: '14px' }}>
              Total: {userPermissions.length} permissions
              {selectedPermissions.length > 0 && (
                <span style={{ color: '#DC2626', marginLeft: '8px' }}>
                  • {selectedPermissions.length} selected for removal
                </span>
              )}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {selectedPermissions.length > 0 && (
              <button
                onClick={handleRevokeSelected}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: loading ? '#ccc' : '#DC2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {loading ? 'Removing...' : `Remove ${selectedPermissions.length}`}
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#9CA3AF',
                padding: '0',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {userPermissions.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#9CA3AF',
              fontSize: '16px'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
              <div>No permissions assigned to this user</div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gap: '12px'
            }}>
              {userPermissions.map((permission, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px',
                    backgroundColor: selectedPermissions.includes(permission.id) ? '#FEF2F2' : '#F9FAFB',
                    border: selectedPermissions.includes(permission.id) ? '1px solid #FCA5A5' : '1px solid #E5E7EB',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <input
                      type="checkbox"
                      checked={selectedPermissions.includes(permission.id)}
                      onChange={() => handlePermissionToggle(permission.id)}
                      style={{
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: '500',
                        color: '#374151',
                        fontSize: '14px',
                        marginBottom: '4px'
                      }}>
                        {permission.name}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: '#6B778C',
                        fontFamily: 'monospace',
                        backgroundColor: '#F3F4F6',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        display: 'inline-block'
                      }}>
                        {permission.code}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      fontSize: '12px',
                      color: '#6B778C',
                      textAlign: 'right',
                      minWidth: '120px'
                    }}>
                      {(permission.module_name && permission.submodule_name) || (permission.module && permission.submodule) ? (
                        <div>
                          <div style={{
                            fontWeight: '500',
                            color: '#059669',
                            marginBottom: '2px'
                          }}>
                            {permission.module_name || permission.module}
                          </div>
                          <div style={{
                            fontSize: '10px',
                            color: '#6B7280'
                          }}>
                            {permission.submodule_name || permission.submodule}
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          fontStyle: 'italic',
                          color: '#7F56DA',
                          fontWeight: '500'
                        }}>
                          Independent
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handlePermissionToggle(permission.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#DC2626',
                        cursor: 'pointer',
                        fontSize: '18px',
                        padding: '4px',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '28px',
                        height: '28px'
                      }}
                      title="Click to select for removal"
                    >
                      {selectedPermissions.includes(permission.id) ? '✓' : '🗑️'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          paddingTop: '20px',
          borderTop: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '20px'
        }}>
          <div style={{ fontSize: '12px', color: '#6B7280' }}>
            {userPermissions.length > 0 ? (
              selectedPermissions.length > 0 ? (
                `${selectedPermissions.length} of ${userPermissions.length} selected for removal`
              ) : (
                `Click checkboxes or 🗑️ icons to select permissions for removal`
              )
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {selectedPermissions.length > 0 && (
              <button
                onClick={() => setSelectedPermissions([])}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Clear Selection
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6B7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserCurrentPermissionsModal;
